import React from 'react';
import { useCurrencySymbol } from '../../../../../utils/currencyUtils';
import {
    parseAmountNumber,
    formatAmountComma,
} from '../../../../../utils/decimalAmountUtils';
import {
  canonicalInstallationAssociatedTaskDescription,
  isTrainingExpenseDisplayRow,
  sumInstallationAssociatedTrainingValues,
} from '../../../../../utils/trainingOtherExpenseSync';

const SoftwareTypeServiceTable = ({ matchedProjects, filteredQuotation, currentPerson, tasks, AssociatedRef }) => {

    const currency = useCurrencySymbol();
    const formatMoneyComma = formatAmountComma;

    const softwarePersons = currentPerson;

    // Group persons by manager and their operations
    const groupPersonsByManager = () => {
        const managers = softwarePersons.filter(p => p.roles?.[0] === "Project manager");
        const operations = softwarePersons.filter(p => p.roles?.[0] === "Operations");

        const grouped = managers.map(manager => {
            // Find operations persons that belong to this manager (same ProjectID)
            const managerOperations = operations.filter(op => op.ProjectID === manager.ProjectID);

            return {
                manager,
                operations: managerOperations
            };
        });

        return grouped;
    };

    // Get person display name with manager-specific numbering
    // Get person display name with manager-specific numbering
    const getPersonDisplayName = (person) => {
        const serviceType = person.typeService || "Software";
        const role = person.roles?.[0] || "";

        if (role === "Project manager") {
            // Find manager number
            const managers = softwarePersons.filter(p => p.roles?.[0] === "Project manager");
            const managerNumber = managers.findIndex(p => p.personId === person.personId) + 1;
            return `${serviceType} Manager ${managerNumber}`;
        } else if (role === "Operations") {
            // Find which manager this operations person belongs to and their number within that manager
            const grouped = groupPersonsByManager();

            for (const group of grouped) {
                const operationIndex = group.operations.findIndex(op => op.personId === person.personId);
                if (operationIndex !== -1) {
                    // Find manager number for this group
                    // const managers = softwarePersons.filter(p => p.roles?.[0] === "Project manager");
                    // const managerNumber = managers.findIndex(p => p.personId === group.manager.personId) + 1;
                    return `${serviceType} Employee ${operationIndex + 1}`;
                }
            }

            // Fallback if no manager found
            return `${serviceType} Employee`;
        } else {
            return `${serviceType} ${role}`;
        }
    };

    // Get ALL tasks for ALL persons (regardless of project)
    const getAllPersonsTasks = () => {
        return softwarePersons.flatMap(person =>
            tasks.filter(task => task.taskownerid === person.personId && task.projectid === person.ProjectID)
        );
    };

    const allPersonsTasks = getAllPersonsTasks();

    const canonicalDesc = (d) =>
        canonicalInstallationAssociatedTaskDescription(d == null ? '' : String(d), currency);

    const labelSet = new Set();
    allPersonsTasks.forEach((task) => {
        const c = canonicalDesc(task.description);
        if (c) labelSet.add(c);
    });
    const labels = [...labelSet];
    const trainingLabel = labels.find((x) => isTrainingExpenseDisplayRow({ description: x }));
    const allDescriptions = trainingLabel
        ? [
            trainingLabel,
            ...labels.filter((x) => x !== trainingLabel).sort((a, b) => a.localeCompare(b)),
        ]
        : labels.sort((a, b) => a.localeCompare(b));

    // Helper function to get total for a specific description and person
    const getDescriptionTotalForPerson = (description, personId) => {
        const matchingTasks = allPersonsTasks.filter(
            (task) =>
                task.taskownerid === personId &&
                canonicalDesc(task.description) === description
        );
        if (isTrainingExpenseDisplayRow({ description })) {
            return sumInstallationAssociatedTrainingValues(matchingTasks);
        }
        return matchingTasks.reduce((sum, task) => sum + parseAmountNumber(task?.value), 0);
    };

    // Calculate total cost for each person
    const getPersonTotalCost = (personId) => {
        return allPersonsTasks
            .filter(task => task.taskownerid === personId)
            .reduce((sum, task) => sum + parseAmountNumber(task?.value), 0);
    };

    return (
        allDescriptions.length ? (
            <div ref={AssociatedRef} className='pt-5 pl-2'>
                <div className="text-lg font-semibold pb-2">
                    <h2>Associated Costs: {currentPerson[0]?.typeService}</h2>
                </div>

                {/* Vertical Table for Multiple Users */}
                <div className="inline-block">
                    {/* Header Row */}
                    <div className="flex font-semibold text-xs border-x">
                        <div className="w-40 bg-[#e8e8e8] p-3 flex border-b justify-start align-middle items-center">Task</div>
                        {softwarePersons.map((person, index) => (
                            <div key={index} className="w-40 bg-[#e8e8e8] border-b capitalize p-3  text-center flex justify-center align-middle items-center">
                                {getPersonDisplayName(person)}<br />
                                {/* <div className="text-xs opacity-80">({person.personName})</div> */}
                            </div>
                        ))}
                    </div>
                    {/* Totals Row */}
                    <div className="flex text-xs border-x">
                        <div className="w-40 p-3 flex justify-start align-middle border-b font-semibold items-center">Total ({currency})</div>
                        {softwarePersons.map((person, index) => (
                            <div key={index} className="w-40 p-3 text-center border-b font-semibold flex justify-center align-middle items-center">
                                {formatMoneyComma(getPersonTotalCost(person.personId))}
                            </div>
                        ))}
                    </div>
                    {/* Scrollable Descriptions */}
                    <div className="max-h-60 narrow-scrollbar overflow-auto inline-block border-x">
                        {allDescriptions.map((description, index) => (
                            <div
                                key={index}
                                className={`flex text-xs `}
                            >
                                <div className="w-40 p-3 flex  border-b justify-start align-middle items-center">
                                    {description}
                                </div>
                                {softwarePersons.map((person, personIndex) => {
                                    const descriptionTotal = getDescriptionTotalForPerson(description, person.personId);
                                    return (
                                        <div key={personIndex} className="w-40 p-3 border-b text-center flex justify-center align-middle items-center">
                                            {descriptionTotal > 0 ? formatMoneyComma(descriptionTotal) : '-'}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        ) : (
            <>
                <div ref={AssociatedRef} className='pt-5 pl-2'>
                    <div className="text-lg font-semibold pb-2">
                        <h2>Associated Costs: {currentPerson[0]?.typeService}</h2>
                    </div>

                        <div className="w-full bg-white flex h-10 border-b">
                            <div className="w-full py-3 flex items-center justify-start text-start text-red-500 text-sm font-semibold">
                                No data available
                            </div>
                        </div>
                </div>
            </>
        )
    );
};

export default SoftwareTypeServiceTable;


